import { useState, useMemo } from "react";
import { Search, AlertTriangle } from "lucide-react";
import { Input, ScrollArea, Skeleton } from "@fox1ck-org/ui";
import { ApiError } from "@/lib/api-error";
import type { OrgUnit } from "@/types/org";
import { OrgTreeNode } from "./OrgTreeNode";

interface OrgTreePanelProps {
  tree: OrgUnit[];
  isLoading: boolean;
  error: Error | null;
  selectedId?: string;
  onSelect: (id: string) => void;
}

function filterTree(nodes: OrgUnit[], query: string): OrgUnit[] {
  const lower = query.toLowerCase();
  return nodes.reduce<OrgUnit[]>((acc, node) => {
    const childMatches = node.children ? filterTree(node.children, query) : [];
    const selfMatches = node.name.toLowerCase().includes(lower) || node.slug.toLowerCase().includes(lower);

    if (selfMatches || childMatches.length > 0) {
      acc.push({
        ...node,
        children: selfMatches ? node.children : childMatches,
      });
    }
    return acc;
  }, []);
}

function TreeSkeleton() {
  return (
    <div className="space-y-1 p-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-2 px-2 py-1.5" style={{ paddingLeft: `${(i % 3) * 12 + 8}px` }}>
          <Skeleton className="h-3.5 w-3.5 rounded shrink-0" />
          <Skeleton className="h-3.5 w-3.5 rounded shrink-0" />
          <Skeleton className="h-3.5 flex-1 rounded" />
        </div>
      ))}
    </div>
  );
}

export function OrgTreePanel({ tree, isLoading, error, selectedId, onSelect }: OrgTreePanelProps) {
  const [search, setSearch] = useState("");

  const filteredTree = useMemo(() => {
    if (!search.trim()) return tree;
    return filterTree(tree, search.trim());
  }, [tree, search]);

  return (
    <div className="flex flex-col h-full">
      <div className="p-2 border-b">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Поиск подразделений…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-1">
          {isLoading ? (
            <TreeSkeleton />
          ) : error ? (
            <TreeError error={error} />
          ) : filteredTree.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">
              {search ? "Ничего не нашлось" : "Подразделений пока нет"}
            </p>
          ) : (
            filteredTree.map((node) => (
              <OrgTreeNode
                key={node.id}
                node={node}
                selectedId={selectedId}
                depth={0}
                onSelect={onSelect}
              />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

function TreeError({ error }: { error: Error }) {
  const apiError = error instanceof ApiError ? error : null;
  return (
    <div className="px-3 py-6 space-y-2 text-xs">
      <div className="flex items-center gap-2 text-destructive font-medium">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        <span>Не удалось загрузить оргструктуру</span>
      </div>
      <p className="text-muted-foreground break-words font-mono">
        {error.message}
      </p>
      {apiError && (
        <p className="text-muted-foreground/70 text-[10px] uppercase tracking-wide">
          {apiError.code}
          {apiError.driverCode ? ` · ${apiError.driverCode}` : ""}
          {` · HTTP ${apiError.status}`}
        </p>
      )}
    </div>
  );
}
